import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface PendingPayment {
  id: string;
  plan_id: string;
  amount_cents: number;
  currency: string;
  type: string;
  provider: string;
  status: string;
  created_at: string;
  plan: {
    id: string;
    code: string;
    name: string;
    billing_interval: string | null;
    billing_type: string;
    price_cents: number;
  };
}

export function usePendingPayment() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: pendingPayment, isLoading } = useQuery({
    queryKey: ["pending-payment", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from("payments")
        .select(`
          id, plan_id, amount_cents, currency, type, provider, status, created_at,
          plan:plans(id, code, name, billing_interval, billing_type, price_cents)
        `)
        .eq("user_id", user.id)
        .eq("status", "pending_wire")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error("Error fetching pending payment:", error);
        return null;
      }

      return data as unknown as PendingPayment | null;
    },
    enabled: !!user?.id,
  });

  const cancelMutation = useMutation({
    mutationFn: async (paymentId: string) => {
      const { data, error } = await supabase.functions.invoke("cancel-pending-payment", {
        body: { payment_id: paymentId },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("Virement en attente annulé");
      queryClient.invalidateQueries({ queryKey: ["pending-payment"] });
    },
    onError: () => {
      toast.error("Erreur lors de l'annulation du virement");
    },
  });

  return {
    pendingPayment,
    isLoading,
    cancelPendingPayment: cancelMutation.mutate,
    isCancelling: cancelMutation.isPending,
  };
}
